import { EventType } from "@ac-expo/api-types";
import { END_HOUR, START_HOUR, toDatabaseFormat } from "@ac-expo/utils";
import { isSameDay, max, parse } from "date-fns";
import { z } from "zod";
import { detailledDateSchema } from "../date-time.schema";

export const UpsertActivitySchema = z.object({
  id: z.string(),
  name: z.string().min(1, "Le nom de l'animation est requis"),
  description: z.string(),
  dates: z
    .array(detailledDateSchema)
    .min(1)
    .superRefine((detailledDates, ctx) => {
      const todayDate = new Date();
      const nowHour = todayDate.getHours();
      detailledDates.forEach((date, index) => {
        const isToday = isSameDay(date.day, todayDate);
        if (isToday && date.start.hour < nowHour && !date.isFullDay) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Vous ne pouvez pas créer une animation rétroactivement",
            path: [index, "start"],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: "Vous ne pouvez pas créer une animation rétroactivement",
            path: [index, "day"],
          });
        }
      });
    }),
  isRegistrationRequired: z.boolean(),
  maxParticipantNumber: z.coerce.number(),
  entry: z.enum(["free", "paid"]),
  place: z.string(),
  structureSchedule: z.boolean().optional(),
  organiserSchedule: z.boolean().optional(),
  activityArea: z
    .object({
      isOwnStand: z
        .union([z.string(), z.boolean()])
        .transform((val) => {
          if (typeof val === "boolean") return val;
          return val === "true";
        })
        .optional(),
      areaName: z.string().optional(),
      area_id: z.string().optional(),
    })
    .optional(),
  structureId: z.string().optional().nullable(),
});

export const getDefaultUpsertActivityDateValues = (
  dates: EventType["dates"],
) => {
  const firstSchedulesDate = dates?.schedules[0]?.fromDate ?? new Date();
  const firstDate = max([firstSchedulesDate, new Date()]);
  const schedule = dates?.schedules[0];
  if (!schedule || schedule.timetables.length === 0) {
    return {
      day: toDatabaseFormat(firstDate),
      start: { hour: START_HOUR, minute: 0 },
      end: { hour: END_HOUR, minute: 0 },
    };
  }
  const start = parse(
    schedule.timetables[0].openingTime ?? "00:00",
    "HH:mm",
    new Date(),
  );
  const end = parse(
    schedule.timetables[0].closingTime ?? "23:59",
    "HH:mm",
    new Date(),
  );
  return {
    day: toDatabaseFormat(firstDate),
    start: { hour: start.getHours(), minute: start.getMinutes() },
    end: { hour: end.getHours(), minute: end.getMinutes() },
  };
};

export const getDefaultUpsertActivityValues = (
  dates: EventType["dates"],
  structureId?: string,
) => {
  return {
    ...defaultUpsertActivityValues,
    structureId,
    dates: [getDefaultUpsertActivityDateValues(dates)],
  };
};

export const defaultUpsertActivityValues = {
  id: "",
  name: "",
  description: "",
  dates: [],
  isRegistrationRequired: false,
  maxParticipantNumber: Infinity,
  entry: "free" as const,
  place: "",
  structureSchedule: false,
  organiserSchedule: false,
  activityArea: {
    isOwnStand: false,
    areaName: "",
    area_id: "",
  },
};

export type UpsertActivityForm = z.infer<typeof UpsertActivitySchema>;
